
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatEnum } from '../../enum/date-format.enum';
import { DynamicTableColumnModel } from '../../models/dto/core/dynamicTableColumn.model';
import { PdfExportRequest } from '../../models/rest/core/pdf-export-request.model';

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { StringUtils } from '../../utils/string.utils';
import { ArrayUtils } from '../../utils/array.utils';
import { DateUtils } from '../../utils/date.utils';

@Injectable({
  providedIn: 'root'
})
export class PdfService {


  constructor(private translate: TranslateService) {
    // doc.addFileToVFS('Roboto-Regular-normal.ttf', this.font);
    // doc.addFont('Roboto-Regular-normal.ttf', 'Roboto-Regular', 'normal');
    // doc.setFont('Roboto-Regular', 'normal');
  }

  exportPdfWithModel(request: PdfExportRequest) {

    if (request.data.length == 0) {
      throw Error(this.translate.instant("message.error.there-is-no-record"));
    }

    if (!request.columns || request.columns.length === 0) {
      throw Error(this.translate.instant("message.error.no-columns-defined"));
    }

    let totalRowsAdd = false;
    request.columns = request.columns.filter(x => (x.isExportExcel || x.isExportExcel == null));

    const columnStyles = {};
    request.columns.forEach((column, index) => {
      columnStyles[index] = { halign: column.align ?? 'center' };
      if (column.totalsRowFunction)
        totalRowsAdd = true;
    });

    const headers: string[] = request.columns.map((column) => column.header || '');
    const tableData = request.data.map((row) => request.columns!.map((column) => ({ content: this.formatCell(row[column.field || ''], column) })));

    if (totalRowsAdd) {
      this.addSumRow(request.columns, request.data, tableData);
    }

    const fontPath = 'assets/fonts/Roboto/Roboto-Regular.ttf';

    const doc = new jsPDF({ orientation: request.isLandscape ? 'landscape' : 'portrait' });
    // Tabloyu oluşturmak için gerekli stil ve özellikleri tanımlayın
    const tableStyle = {
      font: 'Custom',
      fontStyle: 'normal',
      lineHeight: 1.5,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
      fontSize: 10,
      cellPadding: 1,
      overflow: 'linebreak',
    };
    const headerStyle = {
      textColor: [255, 255, 255], // Beyaz yazı rengi
      fillColor: [42, 118, 210], // Arkaplan rengi #2A76D2
    };

    // Türkçe karakter sorununu çözmek için özel bir fonksiyon tanımlayın
    const handleSpecialCharacters = function (cell, data) {
      const specialCharacters = {
        '\u011F': 'ğ',
        '\u011E': 'Ğ',
        '\u00FC': 'ü',
        '\u00DC': 'Ü',
        '\u015F': 'ş',
        '\u015E': 'Ş',
        '\u00F6': 'ö',
        '\u00D6': 'Ö',
        '\u00E7': 'ç',
        '\u00C7': 'Ç',
        '\u0131': 'ı',
        '\u0130': 'İ',
      };

      if (specialCharacters[cell.raw] !== undefined) {
        cell.text = specialCharacters[cell.raw];
      }
    };

    // Custom font'u ekle
    doc.addFont(fontPath, 'Custom', 'normal');
    doc.addFont(fontPath, 'Custom', 'bold');
    doc.setFont('Custom');
    doc.setFontSize(10); // Yazı boyutunu 10 olarak ayarlayın

    const startX = 15; // Tablonun başlangıç x koordinatı
    const startY = 10; // Tablonun başlangıç y koordinatı
    const startXProcessDate = request.isLandscape ? 280 : 190; // Tablonun başlangıç x koordinatı
    const titleWidth = 180; // Tablo genişliği
    const cellHeight = 10; // Hücre yüksekliği

    doc.setFont("Custom", "bold"); // Yazı tipini ve kalınlığını ayarla
    doc.setFontSize(14); // Yazı boyutunu ayarla

    doc.text(request.title, startX, startY, { align: "left" }); // Başlık satırını sola hizala, kalın olarak göster
    doc.setFontSize(10); // Yazı boyutunu ayarla
    doc.text(DateUtils.toFormat(new Date(), DateFormatEnum.DATE_TIME_FORMAT), startXProcessDate, startY, { align: 'right' }); // İşlem tarihini sağa hizala

    // doc.rect(startX, startY + cellHeight, tableWidth, cellHeight, 'S'); // Başlık satırının altında çizgi çizme

    // Add customer name and report dates below the title and process date
    let additionalInfoY = startY + cellHeight; // Adjust the Y position as needed
    if (StringUtils.isNotNull(request.customerName)) {
      doc.setFontSize(12); // Set a slightly larger font size for this additional info
      doc.text(request.customerName!, startX, additionalInfoY, { align: 'left' });
    }

    let reportDate = "";
    if (request.isDateActive) {
      reportDate = DateUtils.toFormat(request.startDate, DateFormatEnum.DATE_FORMAT);
      if (!DateUtils.isSameDate(request.startDate, request.endDate))
        reportDate = reportDate + " - " + DateUtils.toFormat(request.endDate, DateFormatEnum.DATE_FORMAT)
    }
    else {
      reportDate = this.translate.instant("dateRangeEnumValue.ALL");
    }
    doc.text(reportDate, startXProcessDate, additionalInfoY, { align: 'right' });
    additionalInfoY += cellHeight;

    // Tabloyu oluşturun
    doc['autoTable']({
      head: [headers],
      body: tableData,
      startY: additionalInfoY, // Başlık satırının altından başlama
      headStyles: headerStyle,
      styles: tableStyle,
      didParseCell: handleSpecialCharacters,
      columnStyles: columnStyles, // Sütun hizalamalarını belirt
    });
    const pageCount = doc.internal.pages.length - 1;// sayfa sayısı 1 fazla çıkıyordu.

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`${i}/${pageCount}`, doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
    }


    doc.save(request.title.replace(' ', '_') + '_' + new Date().toISOString() + '.pdf');

  }

  // exportPdfForTable(title: string, data: any[], columns: DynamicTableColumnModel[], isLandscape: boolean = false) {

  //   if (data.length == 0) {
  //     throw Error(this.translate.instant("message.error.there-is-no-record"));
  //   }

  //   let totalRowsAdd = false;
  //   columns = columns.filter(x => (x.isExportExcel || x.isExportExcel == null));

  //   const columnStyles = {};
  //   columns.forEach((column, index) => {
  //     columnStyles[index] = { halign: column.align ?? 'center' };
  //     if (column.totalsRowFunction)
  //       totalRowsAdd = true;
  //   });

  //   const headers: string[] = columns.map((column) => column.header);
  //   const tableData = data.map((row) => columns.map((column) => ({ content: this.formatCell(row[column.field], column) })));

  //   if (totalRowsAdd) {
  //     this.addSumRow(columns, data, tableData);
  //   }

  //   const fontPath = 'assets/fonts/Roboto/Roboto-Regular.ttf';

  //   const doc = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait' });
  //   // Tabloyu oluşturmak için gerekli stil ve özellikleri tanımlayın
  //   const tableStyle = {
  //     font: 'Custom',
  //     fontStyle: 'normal',
  //     lineHeight: 1.5,
  //     lineWidth: 0.1,
  //     lineColor: [0, 0, 0],
  //     textColor: [0, 0, 0],
  //     fontSize: 10,
  //     cellPadding: 1,
  //     overflow: 'linebreak',
  //   };
  //   const headerStyle = {
  //     textColor: [255, 255, 255], // Beyaz yazı rengi
  //     fillColor: [42, 118, 210], // Arkaplan rengi #2A76D2
  //   };

  //   // Türkçe karakter sorununu çözmek için özel bir fonksiyon tanımlayın
  //   const handleSpecialCharacters = function (cell, data) {
  //     const specialCharacters = {
  //       '\u011F': 'ğ',
  //       '\u011E': 'Ğ',
  //       '\u00FC': 'ü',
  //       '\u00DC': 'Ü',
  //       '\u015F': 'ş',
  //       '\u015E': 'Ş',
  //       '\u00F6': 'ö',
  //       '\u00D6': 'Ö',
  //       '\u00E7': 'ç',
  //       '\u00C7': 'Ç',
  //       '\u0131': 'ı',
  //       '\u0130': 'İ',
  //     };

  //     if (specialCharacters[cell.raw] !== undefined) {
  //       cell.text = specialCharacters[cell.raw];
  //     }
  //   };

  //   // Custom font'u ekle
  //   doc.addFont(fontPath, 'Custom', 'normal');
  //   doc.addFont(fontPath, 'Custom', 'bold');
  //   doc.setFont('Custom');
  //   doc.setFontSize(10); // Yazı boyutunu 10 olarak ayarlayın

  //   const startX = 15; // Tablonun başlangıç x koordinatı
  //   const startY = 10; // Tablonun başlangıç y koordinatı
  //   const startXProcessDate = isLandscape ? 280 : 190; // Tablonun başlangıç x koordinatı
  //   const titleWidth = 180; // Tablo genişliği
  //   const cellHeight = 10; // Hücre yüksekliği

  //   doc.setFont("Custom", "bold"); // Yazı tipini ve kalınlığını ayarla
  //   doc.setFontSize(14); // Yazı boyutunu ayarla

  //   doc.text(title, startX, startY, { align: "left" }); // Başlık satırını sola hizala, kalın olarak göster
  //   doc.setFontSize(10); // Yazı boyutunu ayarla
  //   doc.text(new Date().toFormat(DateFormatEnum.DATE_TIME_FORMAT), startXProcessDate, startY, { align: 'right' }); // İşlem tarihini sağa hizala

  //   // doc.rect(startX, startY + cellHeight, tableWidth, cellHeight, 'S'); // Başlık satırının altında çizgi çizme

  //   // Add customer name and report dates below the title and process date
  //   const additionalInfoY = startY + 2 * cellHeight; // Adjust the Y position as needed
  //   doc.setFontSize(12); // Set a slightly larger font size for this additional info
  //   doc.text(`Müşteri Adı: customerName`, startX, additionalInfoY, { align: 'left' });
  //   doc.text(`Rapor Tarihleri: reportStartDate - reportEndDate`, startX, additionalInfoY + cellHeight, { align: 'left' });

  //   // Tabloyu oluşturun
  //   doc['autoTable']({
  //     head: [headers],
  //     body: tableData,
  //     startY: additionalInfoY + 2 * cellHeight, // Başlık satırının altından başlama
  //     headStyles: headerStyle,
  //     styles: tableStyle,
  //     didParseCell: handleSpecialCharacters,
  //     columnStyles: columnStyles, // Sütun hizalamalarını belirt
  //   });
  //   const pageCount = doc.internal.pages.length - 1;// sayfa sayısı 1 fazla çıkıyordu.

  //   for (let i = 1; i <= pageCount; i++) {
  //     doc.setPage(i);
  //     doc.text(`${i}/${pageCount}`, doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
  //   }


  //   doc.save(title.replace(' ', '_') + '_' + new Date().toISOString() + '.pdf');
  // }


  private addSumRow(columns: DynamicTableColumnModel[], data: any[], tableData: { content: string; }[][]) {
    let cols: any[] = [];
    let colIndex = 0;
    columns.forEach(c => {
      colIndex++;
      if (colIndex == 1 && !c.totalsRowFunction)
        cols.push(this.translate.instant('common.total'));
      else if (c.totalsRowFunction && c.totalsRowFunction == 'sum')
        cols.push(Number(ArrayUtils.sumByField(data, c.field))?.toFixed(c.roundDecimal ?? 2));
      else if (c.totalsRowFunction && c.totalsRowFunction == 'count')
        cols.push(data.length);
      else
        cols.push('');
    });

    tableData.push(cols);
  }

  private formatCell(value: any, x: DynamicTableColumnModel): string {
    if (value === undefined)
      return "";

    if (x.dateFormat && value) {
      value = this.formatDate(value, x.dateFormat);

    } else if (x.translateKey) {
      if (value != undefined)
        value = this.translate.instant(x.translateKey + "." + value);
      else
        value = "";
    }
    else if (x.dataType == 'decimal')
      value = Number(value).toFixed(x.roundDecimal ?? 2);
    else if (x.dataType == 'boolean')
      value = this.translate.instant('boolean.' + value);
    else
      value = value;
    return value;

  }

  formatDate(dateString: string, format: string): string {
    format = format.replace('dd', 'DD').replace('yyyy', 'YYYY');
    const date = new Date(dateString);
    const formattedDate = DateUtils.toFormat(date, format);
    return formattedDate;
  }

}
